// ZATCA Setup — one row per branch (API ZatcaController, DB script section 32).
// The branch's certificate, private key and CSIDs come from its
// CertificateInfo.json: the user browses for the file, the browser reads it and
// the API stores it. Seller name, VAT / CR numbers and the national address are
// typed here; switching ZATCA on starts signing and sending the branch's sales
// invoices and returns from the start date.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import LoadingUtils from "../../core/utils/LoadingUtils";
import { setheadertoggle } from "../../redux/toggle";
import {
  getZatcaSetups,
  importZatcaCertificate,
  saveZatcaSetup,
  zatcaError,
} from "../../services/ZatcaApiServices";
import {
  AdminScreen,
  AdminList,
  AxAvatar,
  AxEmptyDetail,
  AxFact,
  AxField,
  AxRefreshIcon,
  AxSectionHead,
  AxStatusKeys,
  AxSwitchRow,
  AxTodoNote,
} from "../../components/adminsitration/ui";

const SORTS = [{ key: "name", label: "Branch" }];

// as the ZATCA SDK / CertificateInfo.json number them
const ENVIRONMENTS = [
  { value: 2, label: "Developer portal (testing)" },
  { value: 1, label: "Simulation" },
  { value: 0, label: "Production" },
];
const envLabel = (v) => ENVIRONMENTS.find((e) => e.value === Number(v))?.label || "—";

const FIELDS = [
  "isEnabled",
  "environmentType",
  "startDate",
  "sellerName",
  "vatNumber",
  "crNumber",
  "streetName",
  "buildingNumber",
  "citySubdivision",
  "cityName",
  "postalZone",
  "countryCode",
];

const toForm = (s) => ({
  isEnabled: !!s?.isEnabled,
  environmentType: s?.environmentType ?? 2,
  startDate: s?.startDate ? dayjs(s.startDate).format("YYYY-MM-DD") : "",
  sellerName: s?.sellerName || "",
  vatNumber: s?.vatNumber || "",
  crNumber: s?.crNumber || "",
  streetName: s?.streetName || "",
  buildingNumber: s?.buildingNumber || "",
  citySubdivision: s?.citySubdivision || "",
  cityName: s?.cityName || "",
  postalZone: s?.postalZone || "",
  countryCode: s?.countryCode || "SA",
});

const UploadIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M8 11V2.5M4.8 5.7L8 2.5l3.2 3.2M2.5 10.5v2.2c0 .5.4.8.8.8h9.4c.4 0 .8-.3.8-.8v-2.2" />
  </svg>
);

const ZatcaSetup = () => {
  const dispatch = useDispatch();
  const { togglehead } = useSelector((state) => state.headerToggle);
  const { userId } = useSelector((state) => state.userauth);
  const [setups, setSetups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [sort, setSort] = useState({ key: "name", dir: 1 });
  const [branchId, setBranchId] = useState();
  const [form, setForm] = useState(toForm(null));
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSetups(await getZatcaSetups());
    } catch (error) {
      toast.error(zatcaError(error, "Could not load the ZATCA setup"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const list = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    return setups
      .filter(
        (s) =>
          !needle ||
          `${s.branchName}`.toLowerCase().includes(needle) ||
          `${s.vatNumber ?? ""}`.toLowerCase().includes(needle) ||
          `${s.sellerName ?? ""}`.toLowerCase().includes(needle)
      )
      .slice()
      .sort(
        (a, b) =>
          String(a.branchName ?? "").localeCompare(String(b.branchName ?? ""), undefined, { sensitivity: "base" }) *
          sort.dir
      );
  }, [setups, searchText, sort]);

  const selected = setups.find((s) => s.branchID === branchId);

  // a fresh copy of the picked branch whenever it (or its saved data) changes
  useEffect(() => {
    setForm(toForm(selected));
  }, [selected]);

  const saved = toForm(selected);
  const dirty = !!selected && FIELDS.some((f) => `${form[f]}` !== `${saved[f]}`);
  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await saveZatcaSetup({
        branchID: selected.branchID,
        ...form,
        environmentType: Number(form.environmentType),
        startDate: form.startDate || null,
        updatedUser: userId?.id ? `${userId.id}` : null,
      });
      toast.success(form.isEnabled ? "Saved — ZATCA is on for this branch" : "Saved");
      await load();
    } catch (error) {
      toast.error(zatcaError(error, "Could not save the ZATCA setup"));
    } finally {
      setSaving(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !selected) return;
    if (file.size > 500000) {
      toast.error("That file is too large to be a CertificateInfo.json");
      return;
    }
    setSaving(true);
    try {
      const content = await file.text();
      const result = await importZatcaCertificate({
        branchID: selected.branchID,
        fileName: file.name,
        content,
        updatedUser: userId?.id ? `${userId.id}` : null,
      });
      toast.success(
        `Certificate imported (${envLabel(result.environmentType)}). ` +
          (result.chainFromFile
            ? `The invoice counter continues from ${result.icv}.`
            : `The branch keeps its own invoice counter (${result.icv}).`),
        { duration: 6000 }
      );
      await load();
    } catch (error) {
      toast.error(zatcaError(error, "Could not import the certificate"), { duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const renderRow = (s) => ({
    name: s.branchName,
    sub: (
      <>
        <span className="code">{s.vatNumber || "No VAT number"}</span>
        <span>{s.hasCertificate ? envLabel(s.environmentType) : "No certificate"}</span>
      </>
    ),
    right: s.isEnabled ? (
      <span className="axa-chip ok">ZATCA on</span>
    ) : s.hasCertificate ? (
      <span className="axa-chip">Off</span>
    ) : (
      <span className="axa-dot" />
    ),
    inactive: !s.isEnabled,
  });

  const validTo = selected?.certificateValidTo ? dayjs(selected.certificateValidTo) : null;
  const expiresSoon = validTo && validTo.diff(dayjs(), "day") < 30;

  return (
    <>
      <LoadingUtils loading={loading || saving} />
      <AdminScreen
        title="ZATCA Setup"
        subtitle="E-invoicing (Phase 2) for each branch: its certificate, seller details and address"
        searchValue={searchText}
        onSearchChange={(e) => setSearchText(e.target.value)}
        onSearchClear={() => setSearchText("")}
        searchPlaceholder="Branch, seller, VAT number…"
        searchHint={null}
        tools={
          <>
            <button type="button" className="axa-iconbtn" title="Reload" onClick={load}>
              <AxRefreshIcon />
            </button>
            <button
              type="button"
              className="axa-iconbtn"
              aria-pressed={!!togglehead}
              title="Collapse the app header"
              onClick={() => dispatch(setheadertoggle(!togglehead))}
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M4 10l4-4 4 4" />
              </svg>
            </button>
          </>
        }
        list={
          <AdminList
            items={list}
            keyOf={(s) => s.branchID}
            selectedKey={branchId}
            onSelect={(s) => setBranchId(branchId === s.branchID ? undefined : s.branchID)}
            renderRow={renderRow}
            sorts={SORTS}
            sort={sort}
            onSort={() => setSort((s) => ({ key: "name", dir: -s.dir }))}
            emptyTitle="No branch matches"
            emptyHint="Try a shorter search."
          />
        }
        detail={
          selected ? (
            <div className="axa-detailform">
              <div className="axa-dhead">
                <AxAvatar name={selected.branchName} />
                <div className="axa-dtitle">
                  <div className="t">
                    <span className="nm">{selected.branchName}</span>
                  </div>
                  <div className="s">
                    {selected.isEnabled
                      ? `ZATCA on since ${selected.startDate ? dayjs(selected.startDate).format("DD MMM YYYY") : "—"} · ${envLabel(selected.environmentType)}`
                      : "ZATCA off"}
                  </div>
                </div>
                {dirty ? <span className="axa-pill dirty">Unsaved</span> : null}
              </div>

              <div className="axa-tabbody">
                <AxSectionHead>Certificate (CertificateInfo.json)</AxSectionHead>
                <div className="axa-facts">
                  <AxFact
                    k="File"
                    v={selected.hasCertificate ? selected.certificateFileName || "CertificateInfo.json" : "Not imported"}
                    s={
                      selected.certificateImportedDate
                        ? `imported ${dayjs(selected.certificateImportedDate).format("DD MMM YYYY, hh:mm A")}`
                        : null
                    }
                  />
                  <AxFact k="Environment" v={selected.hasCertificate ? envLabel(selected.environmentType) : "—"} />
                  <AxFact
                    k="Valid to"
                    v={validTo ? validTo.format("DD MMM YYYY") : "—"}
                    s={expiresSoon ? "renew the certificate soon" : null}
                  />
                  <AxFact k="Invoice counter (ICV)" v={selected.hasCertificate ? selected.icv : "—"} />
                </div>
                {selected.certificateSubject ? (
                  <div className="axa-emptyline">{selected.certificateSubject}</div>
                ) : null}
                <div style={{ margin: "8px 0 4px" }}>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".json,application/json"
                    style={{ display: "none" }}
                    onChange={handleFile}
                  />
                  <button
                    type="button"
                    className="axa-btn"
                    disabled={saving}
                    onClick={() => fileRef.current?.click()}
                  >
                    <UploadIcon />
                    {selected.hasCertificate ? "Replace CertificateInfo.json" : "Import CertificateInfo.json"}
                  </button>
                </div>
                <AxTodoNote>
                  Pick this branch&apos;s CertificateInfo.json (written when the branch was onboarded with ZATCA). The
                  certificate, private key and CSIDs are stored for this branch only; the invoice counter is taken
                  from the file until the branch has signed its first invoice here.
                </AxTodoNote>

                <AxSectionHead>E-invoicing</AxSectionHead>
                <div className="axa-formgrid">
                  <AxSwitchRow
                    label="ZATCA on for this branch"
                    note="Sales invoices and returns dated from the start date are signed and sent to ZATCA. A signed invoice can no longer be edited or deleted."
                    checked={form.isEnabled}
                    onChange={set("isEnabled")}
                    disabled={!selected.hasCertificate}
                  />
                  <AxField label="Environment" required>
                    <select value={form.environmentType} onChange={set("environmentType")}>
                      {ENVIRONMENTS.map((e) => (
                        <option key={e.value} value={e.value}>
                          {e.label}
                        </option>
                      ))}
                    </select>
                  </AxField>
                  <AxField label="Start date" required hint="Invoices dated on or after this go to ZATCA">
                    <input type="date" value={form.startDate} onChange={set("startDate")} />
                  </AxField>
                </div>

                <AxSectionHead>Seller</AxSectionHead>
                <div className="axa-formgrid">
                  <AxField label="Seller name (as registered)" required span={2}>
                    <input type="text" value={form.sellerName} onChange={set("sellerName")} maxLength={200} />
                  </AxField>
                  <AxField label="VAT number" required hint="15 digits, starts and ends with 3">
                    <input
                      type="text"
                      className="mono"
                      value={form.vatNumber}
                      onChange={set("vatNumber")}
                      maxLength={15}
                      inputMode="numeric"
                    />
                  </AxField>
                  <AxField label="CR number" required>
                    <input type="text" className="mono" value={form.crNumber} onChange={set("crNumber")} maxLength={20} />
                  </AxField>
                </div>

                <AxSectionHead>National address</AxSectionHead>
                <div className="axa-formgrid">
                  <AxField label="Street" required span={2}>
                    <input type="text" value={form.streetName} onChange={set("streetName")} maxLength={150} />
                  </AxField>
                  <AxField label="Building number" required hint="4 digits">
                    <input
                      type="text"
                      className="mono"
                      value={form.buildingNumber}
                      onChange={set("buildingNumber")}
                      maxLength={4}
                      inputMode="numeric"
                    />
                  </AxField>
                  <AxField label="District" required>
                    <input type="text" value={form.citySubdivision} onChange={set("citySubdivision")} maxLength={150} />
                  </AxField>
                  <AxField label="City" required>
                    <input type="text" value={form.cityName} onChange={set("cityName")} maxLength={100} />
                  </AxField>
                  <AxField label="Postal code" required hint="5 digits">
                    <input
                      type="text"
                      className="mono"
                      value={form.postalZone}
                      onChange={set("postalZone")}
                      maxLength={5}
                      inputMode="numeric"
                    />
                  </AxField>
                  <AxField label="Country">
                    <input type="text" className="mono" value={form.countryCode} onChange={set("countryCode")} maxLength={2} />
                  </AxField>
                </div>

                <AxSectionHead>Invoices</AxSectionHead>
                <div className="axa-facts">
                  <AxFact k="Signed" v={selected.signedCount} />
                  <AxFact k="Accepted by ZATCA" v={selected.doneCount} />
                  <AxFact k="Waiting to send" v={selected.pendingCount} />
                  <AxFact k="Rejected" v={selected.rejectedCount} />
                </div>
                <div className="axa-emptyline">
                  B2B buyers: a customer with a Saudi VAT number gets a standard (cleared) invoice. Fill in the
                  customer&apos;s Address 1 = street, Address 2 = district, Address 3 = building number, City and PIN =
                  postal code.
                </div>
              </div>

              <div className="axa-dfoot">
                <span className="msg">
                  {!selected.hasCertificate
                    ? "Import the branch's CertificateInfo.json before switching ZATCA on."
                    : dirty
                    ? "You have unsaved changes."
                    : ""}
                </span>
                <span className="sp" />
                <button
                  type="button"
                  className="axa-btn"
                  disabled={!dirty || saving}
                  onClick={() => setForm(toForm(selected))}
                >
                  Undo
                </button>
                <button
                  type="button"
                  className="axa-btn axa-btn-primary"
                  disabled={!dirty || saving}
                  onClick={handleSave}
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <AxEmptyDetail title="Pick a branch">
              Choose a branch on the left to import its ZATCA certificate and fill in its seller details.
            </AxEmptyDetail>
          )
        }
        status={
          <AxStatusKeys
            keys={[{ k: "Click", t: "Pick a branch" }]}
            right={`${setups.filter((s) => s.isEnabled).length} of ${setups.length} branches on ZATCA`}
          />
        }
      />
    </>
  );
};

export default ZatcaSetup;
